import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '~components/ui/accordion';
import { Badge } from '~components/ui/badge';
import { formatDate } from '~utils';
import { type ChangelogNote, type ChangelogRelease, type ChangelogSection, getNotesBySection } from '../utils';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

/** Props for rendering a production release's notes. */
type ReleaseNotesProps = {
  /** The parsed release to display. */
  release: ChangelogRelease;
  /** Whether release sections should render as an accordion with New expanded. */
  layout?: 'stacked' | 'accordion';
};

type NoteListProps = {
  /** Notes to render in the list. */
  notes: ChangelogNote[];
};

type ReleaseSectionProps = {
  /** Category being rendered. */
  section: ChangelogSection;
  /** Notes belonging to the category. */
  notes: ChangelogNote[];
  /** Whether the category should be rendered as an accordion item. */
  collapsible: boolean;
  /** Release version used to create a unique heading id. */
  version: string;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

/** Stable order used when presenting release note categories. */
const SECTION_ORDER: ChangelogSection[] = ['new', 'updates', 'bugFixes'];

/** Human-readable labels for the release note categories. */
const SECTION_LABELS: Record<ChangelogSection, string> = {
  new: 'New',
  updates: 'Updates',
  bugFixes: 'Bug fixes',
};

/** Formats deployment dates as readable UTC calendar dates without a time. */
const RELEASE_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: undefined,
  minute: undefined,
  timeZone: 'UTC',
};

/* -------------------------------------------------------------------------- */
/*                               Sub-components                               */
/* -------------------------------------------------------------------------- */

/** Renders the notes belonging to a single release category. */
const NoteList = ({ notes }: NoteListProps) => (
  <ul className="flex list-disc flex-col gap-2 ps-5 text-sm">
    {notes.map((note) => (
      <li key={`${note.commit}-${note.text}`}>
        <span>{note.text}</span> <code className="text-muted-foreground text-xs">({note.commit})</code>
      </li>
    ))}
  </ul>
);

/** Renders one release category as either a section or accordion item. */
const ReleaseSection = ({ section, notes, collapsible, version }: ReleaseSectionProps) => {
  if (notes.length === 0) return null;

  const headingId = `release-${version}-${section}`;

  if (collapsible) {
    return (
      <AccordionItem value={section}>
        <AccordionTrigger className="py-2.5 text-base">{SECTION_LABELS[section]}</AccordionTrigger>
        <AccordionContent>
          <NoteList notes={notes} />
        </AccordionContent>
      </AccordionItem>
    );
  }

  return (
    <section aria-labelledby={headingId}>
      <h3 id={headingId} className="mb-2 font-semibold text-base">
        {SECTION_LABELS[section]}
      </h3>
      <NoteList notes={notes} />
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Renders a semantic release notes section for a given production release.
 *
 * @param props - Release data and layout options.
 * @returns A semantic release notes section.
 */
const ReleaseNotes = ({ release, layout = 'stacked' }: ReleaseNotesProps) => {
  const notesBySection = getNotesBySection(release);
  const collapsible = layout === 'accordion';

  return (
    <article
      className={collapsible ? 'grid gap-x-8 gap-y-4 border-b pb-8 md:grid-cols-[10rem_1fr]' : 'flex flex-col gap-5'}
      aria-labelledby={`release-${release.version}`}
    >
      <header className="flex flex-col gap-1">
        <h2 id={`release-${release.version}`} className="font-bold text-2xl">
          {collapsible ? <Badge variant="secondary">v{release.version}</Badge> : `v${release.version}`}
        </h2>
        <time dateTime={release.date} className="text-muted-foreground text-sm">
          {formatDate(new Date(`${release.date}T00:00:00Z`), RELEASE_DATE_FORMAT)}
        </time>
      </header>

      {collapsible ? (
        <Accordion type="multiple" defaultValue={['new']}>
          {SECTION_ORDER.map((section) => (
            <ReleaseSection
              key={section}
              section={section}
              notes={notesBySection[section]}
              collapsible
              version={release.version}
            />
          ))}
        </Accordion>
      ) : (
        <div className="flex flex-col gap-5">
          {SECTION_ORDER.map((section) => (
            <ReleaseSection
              key={section}
              section={section}
              notes={notesBySection[section]}
              collapsible={false}
              version={release.version}
            />
          ))}
        </div>
      )}
    </article>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { ReleaseNotes };
